/**
 * Format a date string or Date object to a human-readable format
 * @param {string|Date} dateString - The date to format
 * @param {boolean} includeTime - Whether to include the time in the formatted date
 * @return {string} The formatted date string
 */
export const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(dateString);
  }
};

/**
 * Format a time string or Date object to a human-readable time format
 * @param {string|Date} dateString - The date/time to format
 * @return {string} The formatted time string
 */
export const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }
    
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return String(dateString);
  }
};

/**
 * Format a date string or Date object to a human-readable format with time
 * @param {string|Date} dateString - The date to format
 * @return {string} The formatted date and time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    };
    
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date and time:', error);
    return String(dateString);
  }
};

/**
 * Calculate the duration between two dates and format as a readable string
 * @param {string|Date} startDate - The start date
 * @param {string|Date} endDate - The end date
 * @return {string} The formatted duration
 */
export const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 'N/A';
  
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Invalid date range';
    }
    
    // Calculate duration in milliseconds
    const durationMs = end - start;
    
    // Convert to total seconds
    const totalSeconds = Math.floor(durationMs / 1000);
    
    // Calculate hours, minutes, and seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    // Format the duration string
    let durationStr = '';
    if (hours > 0) {
      durationStr += `${hours} hr${hours !== 1 ? 's' : ''} `;
    }
    if (minutes > 0 || hours > 0) {
      durationStr += `${minutes} min${minutes !== 1 ? 's' : ''} `;
    }
    durationStr += `${seconds} sec${seconds !== 1 ? 's' : ''}`;
    
    return durationStr.trim();
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 'Error calculating duration';
  }
};

/**
 * Calculate and format the time elapsed since a given date
 * @param {string|Date} dateString - The date to calculate from
 * @return {string} The elapsed time in a human-readable format
 */
export const getTimeElapsed = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const milliseconds = now - date;
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days} day${days === 1 ? '' : 's'} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else {
      return 'Just now';
    }
  } catch (error) {
    console.error('Error calculating time elapsed:', error);
    return String(dateString);
  }
};

export const parseSimulatedDate = (dateString) => {
  const [date, time, tz] = dateString.split(" ");
  const localDate = new Date(`${date}T${time}`);
  return localDate;
};